import type { ReactNode } from 'react'

/* =====================================================================
 * MemberDetail — the whole record, for the Secretariat
 * =====================================================================
 * Until 0051 the detail panel showed thirteen fields, because
 * serialise_member still emitted the record as it stood in August: no
 * education, no service, no businesses, five of employment's twenty
 * columns, and none of the personal fields added with the v2 schema. The
 * data was never hidden — the secretariat_read policies have been on all
 * eight tables since 0048 — it simply was not asked for.
 *
 * This renders what 0051 now returns. Every section hides itself when it
 * is empty, so a Petarian with no military service does not get a row of
 * dashes; the panel stays as long as the record is, and no longer.
 *
 * It is deliberately read-only. Corrections go through admin_update_member
 * so they land in the audit log with a reason attached.
 * ===================================================================== */

type Row = Record<string, unknown>

/* ---- Reading jsonb safely -------------------------------------------
 * Every value here arrives as jsonb, so a field can legitimately be a
 * string, a number, a boolean, null, an array or an object. The old panel
 * reached for String(), which printed "[object Object]" into the screen
 * somebody reads before approving a person.
 */

function text(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'string') return v.trim() === '' ? null : v
  if (typeof v === 'number') return String(v)
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return null
}

function rows(v: unknown): Row[] {
  return Array.isArray(v) ? (v.filter((x) => typeof x === 'object' && x !== null) as Row[]) : []
}

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

/** Postgres enum labels are snake_case; people are not. */
function humanise(v: unknown): string | null {
  const s = text(v)
  if (s === null) return null
  if (!/^[a-z0-9]+(_[a-z0-9]+)*$/.test(s)) return s
  const words = s.split('_')
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ')
}

/** "2015 – 2018", "2018 – present", "2015", or null. Never "null – null". */
function span(r: Row): string | null {
  const from = text(r.startYear)
  const to = r.isCurrent === true ? 'present' : text(r.endYear)
  if (from && to) return `${from} – ${to}`
  if (from) return from
  if (to && to !== 'present') return to
  return null
}

function join(parts: (string | null)[], sep = ' · '): string | null {
  const kept = parts.filter((p): p is string => p !== null && p !== '')
  return kept.length ? kept.join(sep) : null
}

function place(r: Row): string | null {
  return join([text(r.city), text(r.country)], ', ')
}

/* ---- Pieces ---------------------------------------------------------- */

function Section({
  title,
  count,
  children,
}: {
  title: string
  count?: number
  children: ReactNode
}) {
  return (
    <div style={{ marginTop: 'var(--s-6)' }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        {title}
        {count !== undefined && count > 0 ? ` (${count})` : ''}
      </div>
      {children}
    </div>
  )
}

/** A definition list that drops its own empty rows. */
function Facts({ items }: { items: [string, string | null][] }) {
  const kept = items.filter((i): i is [string, string] => i[1] !== null)
  if (kept.length === 0) return null
  return (
    <dl className="kv">
      {kept.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>{v}</dd>
        </div>
      ))}
    </dl>
  )
}

/** One repeatable record: a bold line, a detail line, a quiet meta line. */
function Entry({
  head,
  sub,
  meta,
  body,
}: {
  head: string | null
  sub?: string | null
  meta?: string | null
  body?: [string, string | null][]
}) {
  return (
    <div
      style={{
        padding: '8px 0',
        borderTop: '1px solid var(--line, rgba(255,255,255,.08))',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13 }}>{head ?? '—'}</div>
      {sub && <div style={{ fontSize: 12.5 }}>{sub}</div>}
      {meta && (
        <div className="small muted" style={{ marginTop: 2 }}>
          {meta}
        </div>
      )}
      {body && body.some((b) => b[1] !== null) && (
        <div style={{ marginTop: 6 }}>
          <Facts items={body} />
        </div>
      )}
    </div>
  )
}

function Chips({ values }: { values: string[] }) {
  if (values.length === 0) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {values.map((v) => (
        <span
          key={v}
          className="small"
          style={{
            padding: '3px 9px',
            borderRadius: 999,
            border: '1px solid var(--line, rgba(255,255,255,.14))',
          }}
        >
          {humanise(v) ?? v}
        </span>
      ))}
    </div>
  )
}

/* ---- The panel -------------------------------------------------------- */

export default function MemberDetail({ detail }: { detail: Row | null }) {
  if (!detail) {
    return (
      <p className="small muted">
        The full record could not be loaded. It may have been removed, or your
        session may have expired — sign in again and reopen it.
      </p>
    )
  }

  const d = detail
  const experiences = rows(d.experiences)
  const education = rows(d.education)
  const appointments = rows(d.appointments)
  const service = rows(d.militaryService)
  const postings = rows(d.militaryPostings)
  const businesses = rows(d.businesses)
  const certifications = rows(d.certifications)
  const awards = rows(d.awards)
  const memberships = rows(d.memberships)

  // Service and credential numbers are returned separately, because the tier
  // that may see a posting history is not the tier that may see an identifier.
  // Here, where both are present, put each back beside its own record.
  const serviceNumbers = new Map<string, string>()
  for (const r of rows(d.militaryServiceNumbers)) {
    const id = text(r.id)
    const no = text(r.serviceNumber)
    if (id && no) serviceNumbers.set(id, no)
  }
  const credentialNumbers = new Map<string, string>()
  for (const r of rows(d.certificationCredentialNumbers)) {
    const id = text(r.id)
    const no = text(r.credentialNo)
    if (id && no) credentialNumbers.set(id, no)
  }

  const hidden = strings(d.hiddenFields)
  const skills = strings(d.skills)
  const offers = strings(d.networkingOffers)
  const discoverable = strings(d.discoverable)

  // The record is only partly readable when the viewer is neither the member
  // nor the Secretariat. Saying so beats rendering a page of blanks.
  if (d.detailsShared === false) {
    return (
      <>
        <Facts
          items={[
            ['KitNo', text(d.kitNo)],
            ['Official name', text(d.officialName)],
            ['House', text(d.house)],
            ['Joined', text(d.joiningYear)],
            ['Passed out', d.stillAtCollege === true ? 'Still at college' : text(d.passingYear)],
          ]}
        />
        <p className="small muted" style={{ marginTop: 'var(--s-5)' }}>
          The rest of this record is not shared with you. If you are signed in
          as Secretariat staff and seeing this, your account has lost its
          Secretariat role.
        </p>
      </>
    )
  }

  return (
    <>
      <Section title="At Petaro">
        <Facts
          items={[
            ['KitNo', text(d.kitNo)],
            ['Official name', text(d.officialName)],
            ['House', text(d.house)],
            ['Joined', text(d.joiningYear)],
            ['Passed out', d.stillAtCollege === true ? 'Still at college' : text(d.passingYear)],
            [
              'Appointment',
              join([text(d.appointment), text(d.appointmentPeriod)], ' '),
            ],
            [
              'Sports',
              join([text(d.sportsAppointment), text(d.sportsAppointmentPeriod)], ' '),
            ],
          ]}
        />
        {appointments.length > 0 &&
          appointments.map((a, i) => (
            <Entry
              key={text(a.id) ?? i}
              head={text(a.title)}
              sub={humanise(a.kind)}
              meta={text(a.period)}
            />
          ))}
      </Section>

      <Section title="Personal">
        <Facts
          items={[
            ["Father's name", text(d.fatherName)],
            ['Date of birth', text(d.dateOfBirth)],
            ['Gender', humanise(d.gender)],
            ['CNIC', text(d.cnic)],
            ['Biography', text(d.biography)],
          ]}
        />
      </Section>

      <Section title="Contact">
        <Facts
          items={[
            ['Email', text(d.email)],
            ['Mobile', text(d.cellNumber)],
            ['WhatsApp', text(d.whatsappNumber)],
            ['Office phone', text(d.officePhone)],
            ['Fax', text(d.fax)],
            ['Residential', text(d.residentialAddress)],
            ['Office', text(d.officeAddress)],
            ['Current', join([text(d.city), text(d.country)], ', ')],
            ['Permanent', join([text(d.permanentCity), text(d.permanentCountry)], ', ')],
          ]}
        />
      </Section>

      {(text(d.linkedinUrl) ||
        text(d.websiteUrl) ||
        text(d.facebookUrl) ||
        text(d.xHandle)) && (
        <Section title="Links">
          <Facts
            items={[
              ['LinkedIn', text(d.linkedinUrl)],
              ['Website', text(d.websiteUrl)],
              ['Facebook', text(d.facebookUrl)],
              ['X', text(d.xHandle)],
              ['Shown to members', d.showSocial === true ? 'Yes' : 'No'],
            ]}
          />
        </Section>
      )}

      <Section title="Currently">
        <Facts
          items={[
            ['Status', humanise(d.professionalStatus)],
            ['Organisation', text(d.currentOrganization)],
            ['Position', text(d.currentPosition)],
          ]}
        />
      </Section>

      {experiences.length > 0 && (
        <Section title="Employment history" count={experiences.length}>
          {experiences.map((e, i) => (
            <Entry
              key={text(e.id) ?? i}
              head={text(e.organisation)}
              sub={join([text(e.role), text(e.department)], ' — ')}
              meta={join([
                span(e),
                text(e.industry),
                humanise(e.sector),
                humanise(e.employmentType),
                place(e) ?? text(e.location),
              ])}
              body={[
                ['Responsibilities', text(e.responsibilities)],
                ['Achievements', text(e.achievements)],
              ]}
            />
          ))}
        </Section>
      )}

      {education.length > 0 && (
        <Section title="Education" count={education.length}>
          {education.map((e, i) => (
            <Entry
              key={text(e.id) ?? i}
              head={text(e.institution)}
              sub={join([text(e.degree), text(e.fieldOfStudy) ?? text(e.major)], ' — ')}
              meta={join([
                join([text(e.startYear), text(e.graduationYear) ?? text(e.endYear)], ' – '),
                humanise(e.degreeLevel),
                humanise(e.institutionType),
                text(e.campus),
                place(e),
              ])}
              body={[
                ['Department', text(e.department)],
                ['Specialisation', text(e.specialization)],
                ['Minor', text(e.minor)],
                ['Grade', text(e.grade)],
                ['Honours', text(e.honours)],
                ['Thesis', text(e.thesis)],
              ]}
            />
          ))}
        </Section>
      )}

      {service.length > 0 && (
        <Section title="Armed Forces service" count={service.length}>
          {service.map((s, i) => {
            const id = text(s.id)
            return (
              <Entry
                key={id ?? i}
                head={humanise(s.service)}
                sub={join([text(s.corps), text(s.branch), text(s.trade)], ' — ')}
                meta={join([
                  humanise(s.status),
                  text(s.commissioningYear) ? `commissioned ${text(s.commissioningYear)}` : null,
                  text(s.retirementYear) ? `left ${text(s.retirementYear)}` : null,
                ])}
                body={[['Service number', id ? (serviceNumbers.get(id) ?? null) : null]]}
              />
            )
          })}
        </Section>
      )}

      {postings.length > 0 && (
        <Section title="Postings" count={postings.length}>
          {postings.map((p, i) => (
            <Entry
              key={text(p.id) ?? i}
              head={text(p.appointment) ?? text(p.rank) ?? text(p.unit)}
              sub={join([text(p.rank), text(p.unit), text(p.regiment)], ' — ')}
              meta={join([span(p), text(p.formation), text(p.station), place(p)])}
              body={[['Remarks', text(p.remarks)]]}
            />
          ))}
        </Section>
      )}

      {businesses.length > 0 && (
        <Section title="Businesses" count={businesses.length}>
          {businesses.map((b, i) => (
            <Entry
              key={text(b.id) ?? i}
              head={text(b.name)}
              sub={join([text(b.position), text(b.brandName)], ' — ')}
              meta={join([
                span(b),
                text(b.industry),
                humanise(b.businessType),
                place(b),
                text(b.employees) ? `${text(b.employees)} staff` : null,
              ])}
              body={[
                ['Website', text(b.website)],
                ['LinkedIn', text(b.linkedinUrl)],
                ['Description', text(b.description)],
              ]}
            />
          ))}
        </Section>
      )}

      {certifications.length > 0 && (
        <Section title="Certifications" count={certifications.length}>
          {certifications.map((c, i) => {
            const id = text(c.id)
            return (
              <Entry
                key={id ?? i}
                head={text(c.name)}
                sub={text(c.issuer)}
                meta={join([
                  text(c.issuedOn) ? `issued ${text(c.issuedOn)}` : null,
                  text(c.expiresOn) ? `expires ${text(c.expiresOn)}` : null,
                ])}
                body={[
                  ['Credential', id ? (credentialNumbers.get(id) ?? null) : null],
                  ['URL', text(c.credentialUrl)],
                ]}
              />
            )
          })}
        </Section>
      )}

      {awards.length > 0 && (
        <Section title="Awards" count={awards.length}>
          {awards.map((a, i) => (
            <Entry
              key={text(a.id) ?? i}
              head={text(a.name)}
              sub={text(a.organisation)}
              meta={join([text(a.year), text(a.category)])}
              body={[['Description', text(a.description)]]}
            />
          ))}
        </Section>
      )}

      {memberships.length > 0 && (
        <Section title="Professional memberships" count={memberships.length}>
          {memberships.map((p, i) => (
            <Entry
              key={text(p.id) ?? i}
              head={text(p.organisation)}
              sub={join([text(p.position), text(p.membershipType)], ' — ')}
              meta={join([span(p), text(p.website)])}
            />
          ))}
        </Section>
      )}

      {(skills.length > 0 || offers.length > 0 || discoverable.length > 0) && (
        <Section title="Skills & networking">
          {skills.length > 0 && (
            <>
              <div className="small muted" style={{ marginBottom: 4 }}>
                Skills
              </div>
              <Chips values={skills} />
            </>
          )}
          {offers.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="small muted" style={{ marginBottom: 4 }}>
                Available for
              </div>
              <Chips values={offers} />
            </div>
          )}
          {discoverable.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="small muted" style={{ marginBottom: 4 }}>
                Searchable by
              </div>
              <Chips values={discoverable} />
            </div>
          )}
        </Section>
      )}

      <Section title="Record">
        <Facts
          items={[
            ['Status', humanise(d.status)],
            ['Visibility', humanise(d.visibility)],
            ['Completeness', text(d.completeness) ? `${text(d.completeness)}%` : null],
            [
              'Hidden from members',
              hidden.length > 0 ? hidden.map((h) => humanise(h) ?? h).join(', ') : 'Nothing hidden',
            ],
            ['Submitted', text(d.submittedAt)],
            ['Decided', text(d.decidedAt)],
            ['Decision note', text(d.decisionNote)],
            ['Reject reason', text(d.rejectReason)],
            ['On hold because', text(d.suspensionReason)],
            ['Deletion requested', text(d.deletionRequestedAt)],
            ['Deletion reason', text(d.deletionReason)],
            ['Created', text(d.createdAt)],
            ['Last updated', text(d.updatedAt)],
          ]}
        />
      </Section>
    </>
  )
}
